import { useState } from "react";
import { toast } from "react-toastify";
import Select from "react-select";
import { useQueryClient } from "@tanstack/react-query";
import { useUsers } from "../../hooks/useUsers";
import CustomPagination from "../../common/Pagination";
import ConfirmationModal from "../../common/ConfirmationModal";
import DynamicImage from "../../components/PostPreview/PostPreview";
import { TableStatus } from "../../components/TableStatus";

const SEARCH_OPTIONS = [
  { value: "name", label: "Name" },
  { value: "mobile", label: "Mobile" },
  { value: "email", label: "Email" },
];

export default function UserTablePage() {
  const queryClient = useQueryClient();
  const itemsPerPage = 50;

  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [appliedSearchKey, setAppliedSearchKey] = useState("");
  const [appliedSearchValue, setAppliedSearchValue] = useState("");

  const [inputType, setInputType] = useState("");
  const [inputValue, setInputValue] = useState("");

  const [userToDelete, setUserToDelete] = useState(null);

  const {
    users,
    pagination,
    isLoading,
    isFetching,
    isError,
    error,
    deleteUser,
    isDeleting,
    downloadUsersCSV,
    isDownloadingCSV,
  } = useUsers(
    currentPage,
    itemsPerPage,
    appliedSearchKey,
    appliedSearchValue,
    sortBy,
    sortOrder
  );

  const handleSearch = () => {
    if (!inputValue) {
      handleReset();
      return;
    }

    setAppliedSearchKey(inputType || "name");
    setAppliedSearchValue(inputValue);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setInputType("");
    setInputValue("");
    setAppliedSearchKey("");
    setAppliedSearchValue("");
    setCurrentPage(1);
  };

  // const handleManualRefresh = () => {
  //   queryClient.invalidateQueries({ queryKey: ["users"] });
  //   toast.success("Refreshing data...");
  // };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    await deleteUser(userToDelete._id);

    if (users.length === 1 && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
    setUserToDelete(null);
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return null;
    return sortOrder === "asc" ? " ▲" : " ▼";
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-light d-flex justify-content-between align-items-center p-3">
        <h4 className="mb-0 text-primary-emphasis">User Management</h4>
        <div className="d-flex gap-2">
          <span className="badge bg-info text-dark d-flex align-items-center mr-2">
            Registered Users: {pagination?.signedInUsers || 0}
          </span>
          <button
            className="btn btn-outline-success btn-sm mr-2"
            onClick={() => downloadUsersCSV()}
            disabled={isDownloadingCSV}
          >
            {isDownloadingCSV ? "Downloading..." : "Download CSV"}
          </button>
          {/* <button
            className="btn btn-outline-secondary btn-sm"
            onClick={handleManualRefresh}
          >
            Refresh
          </button> */}
        </div>
      </div>

      <div className="card-body border-bottom">
        <div className="row g-2 align-items-center">

          <div className="col-md-3">
            <Select
              options={SEARCH_OPTIONS}
              value={SEARCH_OPTIONS.find((opt) => opt.value === inputType) || null}
              onChange={(selectedOption) =>
                setInputType(selectedOption ? selectedOption.value : "")
              }
              placeholder="Search By..."
              isClearable
              styles={{
                control: (base) => ({ ...base, minHeight: '38px', borderColor: '#dee2e6' }),
              }}
            />
          </div>

          <div className="col-md-4">
            <input
              type="text"
              className="form-control"
              placeholder={inputType === 'mobile' ? "Search Mobile (no +)" : "Search..."}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </div>

          <div className="col-md-2">
            <button
              className="btn btn-primary w-100"
              onClick={handleSearch}
              disabled={isLoading || isFetching}
            >
              {isFetching ? "Searching..." : "Search"}
            </button>
          </div>

          <div className="col-md-2">
            <button
              className="btn btn-outline-dark w-100"
              onClick={handleReset}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      <div className="card-body">
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>No</th>
                <th>Profile</th>
                <th style={{ cursor: "pointer" }} onClick={() => handleSort("name")}>
                  Name {getSortIcon("name")}
                </th>
                <th style={{ cursor: "pointer" }} onClick={() => handleSort("email")}>
                  Email {getSortIcon("email")}
                </th>
                <th>Mobile</th>
                <th style={{ cursor: "pointer" }} onClick={() => handleSort("createdAt")}>
                  Joined {getSortIcon("createdAt")}
                </th>
                <th style={{ cursor: "pointer" }} onClick={() => handleSort("tokenBalance")}>
                  Token {getSortIcon("tokenBalance")}
                </th>
                <th style={{ cursor: "pointer" }} onClick={() => handleSort("miningRate")}>
                  Rate {getSortIcon("miningRate")}
                </th>
                <th>Ref Code</th>
                <th>Ref Users</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              <TableStatus
                status={isLoading ? "loading" : isError ? "failed" : "succeeded"}
                error={error}
                dataLength={users.length}
                colSpan={11}
                loadingText="Loading users..."
                emptyText="No users found."
              />

              {!isLoading && !isError && users.map((user, index) => (
                <tr key={user._id || index}>
                  <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td>
                    <DynamicImage
                      src={user?.profilePic || "/img/user.jpg"}
                      alt={user?.name}
                      style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }}
                    />
                  </td>
                  <td>{user?.name || "-"}</td>
                  <td>{user?.email || "-"}</td>
                  <td>{user?.mobile || "-"}</td>
                  <td>
                    {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : "-"}
                  </td>
                  <td>{user?.tokenBalance ? Number(user.tokenBalance).toFixed(4) : "0"}</td>
                  <td>{user?.miningRate || "-"}</td>
                  <td>{user?.refCode || "-"}</td>
                  <td>{user?.refUsers?.length || 0}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => setUserToDelete(user)}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="card-footer bg-white">
          <CustomPagination
            currentPage={Number(currentPage)}
            totalPages={Number(pagination.totalPages)}
            totalItems={Number(pagination.totalRecords)}
            itemsPerPage={itemsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      )}

      <ConfirmationModal
        show={userToDelete !== null}
        onClose={() => setUserToDelete(null)}
        onConfirm={handleDelete}
        title="Confirm Deletion"
        confirmText="Delete"
        isLoading={isDeleting}
        confirmButtonVariant="danger"
      >
        <p className="text-center">
          Are you sure you want to delete user <strong>{userToDelete?.name || userToDelete?.email}</strong>?
        </p>
      </ConfirmationModal>
    </div>
  );
}